/**
 * L'horloge de **position**.
 *
 * # Pourquoi elle est séparée du ticker
 *
 * Un seul objet portait deux rôles sans rapport : dire l'heure (`currentTime`,
 * lu par `Transport` pour en déduire le tick) et cadencer l'ordonnancement
 * (`register`, appelé toutes les 25 ms pour remettre des notes à l'audio). Les
 * deux tenaient sur `AudioContext.currentTime`, ce qui liait la tête de lecture
 * à l'état du contexte audio : contexte suspendu — onglet en arrière-plan, ou
 * simplement jamais démarré faute de geste de l'utilisateur — et le temps
 * s'arrêtait. En séance, deux machines divergeaient alors sans jamais se
 * rattraper.
 *
 * La position vit donc ici, sur `performance.now()` : monotone,
 * sub-milliseconde, et jamais suspendue. Le ticker vit dans
 * `ScheduleTicker`, et reste sur l'horloge du contexte, qui est la seule
 * exacte à l'échantillon. Le pont entre les deux est `AudioTimebase`.
 *
 * Conséquence assumée : contexte audio suspendu, le roll défile et les notes se
 * dessinent, mais on n'entend rien. C'est le bon compromis — empiler des notes
 * sur une horloge figée les ferait toutes partir ensemble au réveil.
 */
export interface PositionClock {
  /** Secondes écoulées depuis une origine quelconque, mais stable. */
  readonly currentTime: number;
}

/**
 * L'horloge de position telle que le navigateur la donne.
 *
 * En secondes plutôt qu'en millisecondes : tout le reste du moteur compte en
 * secondes, et faire la division ici évite qu'elle soit oubliée ailleurs.
 */
export class PerformanceClock implements PositionClock {
  get currentTime(): number {
    return performance.now() / 1000;
  }
}
